import React, { useEffect, useRef, useState } from "react";

// Child BOQ sheet data layout (custom-boq-table.js)
// DATA_START = row 9 (1-based) → index 8 (0-based)
// Col F (5) = MatPrice, Col H (7) = LabPrice, Col J (9) = EqPrice
const DATA_START_IDX = 8;
const PRICE_COLS = { mat: 5, lab: 7, eq: 9 };

const TYPES = [
  {
    key: "all",
    label: "სამშენებლო\nსამუშაოები",
    cols: [5, 7, 9],
    icon: (
      <svg viewBox="0 0 40 40" width={36} height={36} fill="none">
        <rect x="4" y="24" width="32" height="12" rx="2" stroke="#3b82f6" strokeWidth="2" />
        <rect x="10" y="14" width="20" height="12" rx="1" stroke="#3b82f6" strokeWidth="2" />
        <rect x="16" y="6" width="8" height="10" rx="1" stroke="#3b82f6" strokeWidth="2" />
      </svg>
    ),
  },
  {
    key: "mat",
    label: "საშენი\nმასალები",
    cols: [5],
    icon: (
      <svg viewBox="0 0 40 40" width={36} height={36} fill="none">
        <rect x="4" y="4" width="15" height="15" rx="1" stroke="#3b82f6" strokeWidth="2" />
        <rect x="21" y="4" width="15" height="15" rx="1" stroke="#3b82f6" strokeWidth="2" />
        <rect x="4" y="21" width="15" height="15" rx="1" stroke="#3b82f6" strokeWidth="2" />
        <rect x="21" y="21" width="15" height="15" rx="1" stroke="#3b82f6" strokeWidth="2" />
      </svg>
    ),
  },
  {
    key: "lab",
    label: "ხელფასი,\nსერვისი",
    cols: [7],
    icon: (
      <svg viewBox="0 0 40 40" width={36} height={36} fill="none">
        <circle cx="20" cy="12" r="7" stroke="#3b82f6" strokeWidth="2" />
        <path d="M6 36c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "eq",
    label: "მექანიზმები\nტრანსპ.",
    cols: [9],
    icon: (
      <svg viewBox="0 0 40 40" width={36} height={36} fill="none">
        <rect x="2" y="20" width="28" height="12" rx="2" stroke="#3b82f6" strokeWidth="2" />
        <path d="M8 20V14l8-8h8l6 6v8" stroke="#3b82f6" strokeWidth="2" strokeLinejoin="round" />
        <circle cx="10" cy="34" r="4" stroke="#3b82f6" strokeWidth="2" />
        <circle cx="26" cy="34" r="4" stroke="#3b82f6" strokeWidth="2" />
        <line x1="30" y1="24" x2="38" y2="20" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
];

function getFactor(rule) {
  if (!rule.applied) return 1.0;
  const pct = parseFloat(rule.pct) || 0;
  return rule.direction === "increase" ? 1 + pct / 100 : Math.max(0, 1 - pct / 100);
}

function computeEffectiveFactors(rules) {
  const allF = getFactor(rules.all);
  return {
    5: allF * getFactor(rules.mat),
    7: allF * getFactor(rules.lab),
    9: allF * getFactor(rules.eq),
  };
}

async function captureOriginals() {
  const result = {};
  await Excel.run(async (ctx) => {
    const wb = ctx.workbook;
    wb.worksheets.load("items/name");
    await ctx.sync();

    for (const ws of wb.worksheets.items) {
      if (!ws.name.includes("დეტალური")) continue;

      const range = ws.getRangeByIndexes(DATA_START_IDX, 0, 200, 13);
      range.load("values");
      await ctx.sync();

      const vals = range.values;
      const rowIndices = [];

      for (let i = 0; i < vals.length; i++) {
        const desc = String(vals[i][1] ?? "");
        if (desc.includes("ჯამი")) break;
        if (!desc.trim()) break;
        rowIndices.push(DATA_START_IDX + i);
      }

      if (!rowIndices.length) continue;

      const colData = {};
      for (const col of [5, 7, 9]) {
        colData[col] = rowIndices.map((ri) => {
          const v = vals[ri - DATA_START_IDX][col];
          return typeof v === "number" ? v : 0;
        });
      }
      result[ws.name] = { rowIndices, colData };
    }
  });
  return result;
}

async function writeFactors(originals, factors) {
  await Excel.run(async (ctx) => {
    for (const [sheetName, { rowIndices, colData }] of Object.entries(originals)) {
      const ws = ctx.workbook.worksheets.getItemOrNullObject(sheetName);
      ws.load("name");
      await ctx.sync();
      if (ws.isNullObject) continue;

      for (const [colStr, origVals] of Object.entries(colData)) {
        const col = Number(colStr);
        const factor = factors[col] ?? 1.0;
        const newVals = origVals.map((v) => [+(v * factor).toFixed(4)]);
        ws.getRangeByIndexes(rowIndices[0], col, rowIndices.length, 1).values = newVals;
      }
    }
    await ctx.sync();
  });
}

async function restoreOriginals(originals) {
  await Excel.run(async (ctx) => {
    for (const [sheetName, { rowIndices, colData }] of Object.entries(originals)) {
      const ws = ctx.workbook.worksheets.getItemOrNullObject(sheetName);
      ws.load("name");
      await ctx.sync();
      if (ws.isNullObject) continue;

      for (const [colStr, origVals] of Object.entries(colData)) {
        const col = Number(colStr);
        ws.getRangeByIndexes(rowIndices[0], col, rowIndices.length, 1).values = origVals.map((v) => [v]);
      }
    }
    await ctx.sync();
  });
}

export default function IndexationPanel({ onBack }) {
  const [rules, setRules] = useState({
    all: { direction: "increase", pct: "10", applied: false },
    mat: { direction: "increase", pct: "10", applied: false },
    lab: { direction: "increase", pct: "10", applied: false },
    eq:  { direction: "increase", pct: "10", applied: false },
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [sheetsFound, setSheetsFound] = useState(null);
  const originalsRef = useRef(null);

  useEffect(() => {
    Excel.run(async (ctx) => {
      ctx.workbook.worksheets.load("items/name");
      await ctx.sync();
      const n = ctx.workbook.worksheets.items.filter((ws) => ws.name.includes("დეტალური")).length;
      setSheetsFound(n);
    }).catch(() => setSheetsFound(0));
  }, []);

  const handleApply = async (key) => {
    setBusy(true);
    setStatus("");
    try {
      if (!originalsRef.current) {
        originalsRef.current = await captureOriginals();
      }
      const updated = { ...rules, [key]: { ...rules[key], applied: true } };
      setRules(updated);
      const factors = computeEffectiveFactors(updated);
      await writeFactors(originalsRef.current, factors);
      const n = Object.keys(originalsRef.current).length;
      setStatus(`✓ გამოყენებულია ${n} ფურცელზე`);
    } catch (e) {
      setStatus("შეცდომა: " + (e.message || "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (key) => {
    setBusy(true);
    setStatus("");
    try {
      const updated = { ...rules, [key]: { ...rules[key], applied: false } };
      setRules(updated);

      if (!originalsRef.current) {
        setStatus("↺ ორიგინალი ფასები შენახული არ არის");
        setBusy(false);
        return;
      }

      const anyStillActive = Object.entries(updated).some(([, r]) => r.applied);
      if (anyStillActive) {
        await writeFactors(originalsRef.current, computeEffectiveFactors(updated));
        setStatus("↺ გაუქმებულია, სხვა ინდექსები აქტიურია");
      } else {
        await restoreOriginals(originalsRef.current);
        originalsRef.current = null;
        setStatus("↺ ყველა ფასი ორიგინალზე დაბრუნდა");
      }
    } catch (e) {
      setStatus("შეცდომა: " + (e.message || "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleResetAll = async () => {
    setBusy(true);
    setStatus("");
    try {
      if (originalsRef.current) {
        await restoreOriginals(originalsRef.current);
        originalsRef.current = null;
      }
      setRules({
        all: { direction: "increase", pct: "10", applied: false },
        mat: { direction: "increase", pct: "10", applied: false },
        lab: { direction: "increase", pct: "10", applied: false },
        eq:  { direction: "increase", pct: "10", applied: false },
      });
      setStatus("↺ ყველა ფასი ორიგინალზე დაბრუნდა");
    } catch (e) {
      setStatus("შეცდომა: " + (e.message || "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const anyApplied = Object.values(rules).some((r) => r.applied);

  return (
    <div style={S.root}>
      <div style={S.header}>
        {onBack && (
          <button onClick={onBack} style={S.backBtn}>← უკან</button>
        )}
        <h2 style={S.title}>ინდექსირება</h2>
        {sheetsFound !== null && (
          <span style={S.sheetsBadge}>
            {sheetsFound} ფურცელი
          </span>
        )}
      </div>

      {sheetsFound === 0 && (
        <div style={S.warn}>
          BOQ ბავშვი ფურცლები ვერ მოიძებნა. ჯერ გახსენით BOQ პროექტი.
        </div>
      )}

      <div style={S.rows}>
        {TYPES.map((type) => {
          const rule = rules[type.key];
          return (
            <div key={type.key} style={{ ...S.row, opacity: busy ? 0.7 : 1 }}>
              {/* Icon card */}
              <div style={S.iconCard}>
                {type.icon}
                <span style={S.iconLabel}>{type.label}</span>
              </div>

              {/* Direction dropdown */}
              <select
                value={rule.direction}
                onChange={(e) =>
                  setRules((r) => ({
                    ...r,
                    [type.key]: { ...r[type.key], direction: e.target.value, applied: false },
                  }))
                }
                style={S.select}
                disabled={busy}
              >
                <option value="increase">გაზრდა</option>
                <option value="decrease">შემცირება</option>
              </select>

              {/* Percentage input */}
              <div style={S.pctWrap}>
                <input
                  type="number"
                  min="0"
                  max="200"
                  step="1"
                  value={rule.pct}
                  onChange={(e) =>
                    setRules((r) => ({
                      ...r,
                      [type.key]: { ...r[type.key], pct: e.target.value, applied: false },
                    }))
                  }
                  style={S.pctInput}
                  disabled={busy}
                />
                <span style={S.pctSuffix}>%</span>
              </div>

              {/* Apply */}
              <button
                onClick={() => handleApply(type.key)}
                disabled={busy || sheetsFound === 0}
                style={{
                  ...S.iconBtn,
                  background: rule.applied ? "#065f46" : "#1e3a2a",
                  borderColor: rule.applied ? "#10b981" : "#2d5a3d",
                  color: rule.applied ? "#6ee7b7" : "#4ade80",
                }}
                title="გამოყენება"
              >
                ✓
              </button>

              {/* Reset */}
              <button
                onClick={() => handleReset(type.key)}
                disabled={busy || !rule.applied}
                style={{
                  ...S.iconBtn,
                  background: "#1e1e2e",
                  borderColor: rule.applied ? "#7c3aed" : "#2d2d3d",
                  color: rule.applied ? "#c4b5fd" : "#555",
                  opacity: rule.applied ? 1 : 0.4,
                }}
                title="გაუქმება"
              >
                ↺
              </button>
            </div>
          );
        })}
      </div>

      {/* Status */}
      {status && (
        <div style={{
          ...S.statusBox,
          background: status.startsWith("შეცდ") ? "#3a1a1a" : "#0a2a1a",
          color: status.startsWith("შეცდ") ? "#ff8888" : "#6ee7b7",
        }}>
          {status}
        </div>
      )}

      {/* Reset All */}
      {anyApplied && (
        <button
          onClick={handleResetAll}
          disabled={busy}
          style={S.resetAllBtn}
        >
          ↺ ყველა ინდექსის გაუქმება
        </button>
      )}

      <div style={S.hint}>
        ინდექსირება ცვლის ფასებს ყველა ბავშვ ფურცელში. ↺ ღილაკი აბრუნებს ფასებს
        ორიგინალ მნიშვნელობებზე (სანამ პანელი ღია იყო).
      </div>
    </div>
  );
}

const S = {
  root: {
    padding: "16px 12px",
    minHeight: "100vh",
    background: "var(--bg-primary, #0f172a)",
    color: "var(--text-primary, #e2e8f0)",
    fontFamily: "inherit",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
  },
  backBtn: {
    background: "transparent",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    fontSize: 13,
    padding: "2px 0",
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    flex: 1,
    textAlign: "center",
  },
  sheetsBadge: {
    fontSize: 11,
    background: "#1e3a5f",
    color: "#93c5fd",
    padding: "2px 8px",
    borderRadius: 10,
  },
  warn: {
    background: "#2a1e0a",
    color: "#fbbf24",
    padding: "8px 12px",
    borderRadius: 6,
    fontSize: 12,
    marginBottom: 12,
  },
  rows: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#1e293b",
    borderRadius: 8,
    padding: "8px 10px",
    border: "1px solid #334155",
  },
  iconCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#0f172a",
    border: "2px solid #3b82f6",
    borderRadius: 6,
    padding: "6px 8px",
    minWidth: 60,
    gap: 2,
  },
  iconLabel: {
    fontSize: 9,
    color: "#93c5fd",
    textAlign: "center",
    lineHeight: 1.2,
    whiteSpace: "pre-line",
  },
  select: {
    flex: 1,
    padding: "5px 6px",
    background: "#0f172a",
    border: "1px solid #334155",
    color: "#e2e8f0",
    borderRadius: 5,
    fontSize: 12,
    cursor: "pointer",
  },
  pctWrap: {
    display: "flex",
    alignItems: "center",
    background: "#0f172a",
    border: "2px solid #3b82f6",
    borderRadius: 5,
    padding: "0 6px",
    width: 62,
  },
  pctInput: {
    background: "transparent",
    border: "none",
    color: "#e2e8f0",
    fontSize: 13,
    fontWeight: 600,
    width: 36,
    outline: "none",
    textAlign: "right",
  },
  pctSuffix: {
    color: "#64748b",
    fontSize: 12,
    marginLeft: 2,
  },
  iconBtn: {
    width: 32,
    height: 32,
    border: "1px solid",
    borderRadius: 6,
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.15s",
  },
  statusBox: {
    marginTop: 12,
    padding: "8px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 500,
  },
  resetAllBtn: {
    marginTop: 12,
    width: "100%",
    padding: "9px 0",
    background: "#2d1b4e",
    border: "1px solid #7c3aed",
    color: "#c4b5fd",
    borderRadius: 6,
    fontSize: 13,
    cursor: "pointer",
  },
  hint: {
    marginTop: 16,
    color: "#475569",
    fontSize: 10,
    lineHeight: 1.5,
  },
};
